import { Navigate, Outlet } from 'react-router-dom'
import { useAppSelector } from 'reduxStore/store'
import { IModulePermissionType } from 'types'
import { CircularProgress, Box } from '@mui/material'
import { IProssionalRoleTypeEnum } from 'types/user/enum'

/**
 * IPermissionByModuleNameProps interface defines the properties for the PermissionByModuleName component.
 */
interface IPermissionByModuleNameProps extends React.PropsWithChildren {
  /** The module permissions required to access the children components */
  modulesRequired: IModulePermissionType[]
  restrictedCondition?: 'and' | 'or'
  withOutlet?: boolean
}
interface IRestrictedToEstablishmentProps {
  rolesAllowed?: IProssionalRoleTypeEnum[]
}

/**
 * RestrictedToEstablishment component
 * This component restricts access to its children based on the establishment ID.
 * If the establishment ID is not available, it redirects to the home page.
 * @returns {JSX.Element} The rendered Outlet or a redirect to the home page.
 */
const RestrictedToEstablishment = ({
  rolesAllowed
}: IRestrictedToEstablishmentProps) => {
  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )

  const roleType = useAppSelector(
    rState => rState.establishment.data?.role_type
  )

  // If establishmentId is not available, redirect to home page
  if (!establishmentId) {
    return <Navigate to={'/'} />
  }

  if (rolesAllowed && (!roleType || !rolesAllowed.includes(roleType))) {
    return <Navigate to={'/'} />
  }

  // If establishmentId is available, render the Outlet
  // This allows the nested routes to be rendered
  return <Outlet />
}

export const RestrictedToEstablishmentWithPermission: React.FC<
  IPermissionByModuleNameProps
> = ({ modulesRequired, restrictedCondition = 'or', children, withOutlet }) => {
  const establishmentModules = useAppSelector(
    state => state.establishment.data?.modules_permission
  )

  // If permissions are still loading (undefined), show loading state
  if (establishmentModules === undefined) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='200px'
      >
        <CircularProgress />
      </Box>
    )
  }

  const hasRequiredModule =
    restrictedCondition == 'or'
      ? modulesRequired.some(module => establishmentModules.includes(module))
      : modulesRequired.every(module => establishmentModules.includes(module))

  if (!hasRequiredModule && modulesRequired.length > 0) {
    return <Navigate to={'/'} />
  }

  if (withOutlet) {
    return <Outlet />
  }

  return <>{children}</>
}

export default RestrictedToEstablishment
